import { useEffect, useRef, useState } from "react";
import { ChevronDown, KeyRound, LogOut, ShieldCheck, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { EditorModal } from "@/components/EditorModal";
import { api, formatApiError } from "@/lib/api";

const ROLE_LABELS = {
  system_administrator: "System Administrator",
  operations: "Operations",
  staffing_coordinator: "Staffing Coordinator",
  recruiter: "Recruiter",
  hr: "HR",
  executive: "Executive",
  provider: "Provider",
  client: "Client",
};

export const roleLabel = (role) => ROLE_LABELS[role] || role;

function ChangePasswordForm({ onClose }) {
  const { applySession } = useAuth();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const update = (key) => (event) => setForm((state) => ({ ...state, [key]: event.target.value }));

  async function submit(event) {
    event.preventDefault();
    if (form.next !== form.confirm) {
      setError("New passwords do not match.");
      return;
    }
    setError("");
    setSaving(true);
    try {
      const response = await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      applySession(response.data);
      toast.success("Password updated");
      onClose();
    } catch (requestError) {
      setError(formatApiError(requestError));
      setSaving(false);
    }
  }

  return (
    <section className="inline-edit-panel" data-testid="change-password-panel">
      <div className="section-heading">
        <div>
          <h2>Change password</h2>
          <p>Use at least 8 characters. You stay signed in after the change.</p>
        </div>
        <button aria-label="Close" className="projection-icon-button" data-testid="close-change-password" type="button" onClick={onClose}>
          <X size={16} />
        </button>
      </div>
      {error && <p className="form-message error-message" data-testid="change-password-error">{error}</p>}
      <form className="drawer-form-grid" data-testid="change-password-form" onSubmit={submit}>
        <label className="drawer-form-full">
          <span className="field-label">Current password</span>
          <input autoComplete="current-password" data-testid="current-password-input" required type="password" value={form.current} onChange={update("current")} />
        </label>
        <label>
          <span className="field-label">New password</span>
          <input autoComplete="new-password" data-testid="new-password-input" minLength={8} required type="password" value={form.next} onChange={update("next")} />
        </label>
        <label>
          <span className="field-label">Confirm new password</span>
          <input autoComplete="new-password" data-testid="confirm-password-input" minLength={8} required type="password" value={form.confirm} onChange={update("confirm")} />
        </label>
        <div className="drawer-form-full form-actions">
          <button className="button button-primary" data-testid="save-password-button" disabled={saving} type="submit">
            <KeyRound size={15} /> {saving ? "Saving…" : "Update password"}
          </button>
        </div>
      </form>
    </section>
  );
}

export const UserMenu = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [changing, setChanging] = useState(false);
  const wrapRef = useRef(null);
  const letter = (value) => ((value || "").replace(/[^\p{L}]/gu, "")[0] || "");
  const initials = `${letter(user.first_name)}${letter(user.last_name)}`.toUpperCase();

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => event.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="user-menu-wrap" ref={wrapRef}>
      <button
        aria-expanded={open}
        aria-haspopup="menu"
        className={`profile-button ${open ? "is-open" : ""}`}
        data-testid="user-menu-button"
        title="Account menu"
        type="button"
        onClick={() => setOpen((value) => !value)}
      >
        <span className="profile-avatar" data-testid="user-avatar">{initials}</span>
        <span className="profile-copy">
          <strong data-testid="user-name">{user.first_name} {user.last_name}</strong>
          <small data-testid="user-role">{roleLabel(user.role)}</small>
        </span>
        <ChevronDown size={16} aria-hidden="true" className={open ? "sidebar-chevron-open" : ""} />
      </button>
      {open && (
        <div className="user-menu" data-testid="user-menu" role="menu">
          <div className="user-menu-identity">
            <strong>{user.first_name} {user.last_name}</strong>
            <small data-testid="user-menu-email">{user.email}</small>
            <span className="user-menu-role">{roleLabel(user.role)}</span>
          </div>
          <button className="user-menu-item" data-testid="security-menu-item" role="menuitem" type="button" onClick={() => { setOpen(false); navigate("/account/security"); }}>
            <ShieldCheck size={15} /> Security
          </button>
          <button className="user-menu-item" data-testid="change-password-menu-item" role="menuitem" type="button" onClick={() => { setOpen(false); setChanging(true); }}>
            <KeyRound size={15} /> Change password
          </button>
          <button className="user-menu-item user-menu-signout" data-testid="sign-out-menu-item" role="menuitem" type="button" onClick={logout}>
            <LogOut size={15} /> Sign out
          </button>
        </div>
      )}
      {changing && (
        <EditorModal onClose={() => setChanging(false)} testId="change-password-modal">
          <ChangePasswordForm onClose={() => setChanging(false)} />
        </EditorModal>
      )}
    </div>
  );
};
